import { Address } from "@/types/address.types";
import { ExportOptions, ExportResult, QAddrFileData, EXPORT_CONSTANTS } from "@/types/export.types";
import { addressRepository } from "@/db/repositories/address.repository";
import { compressData } from "@/utils/compression.utils";
import { encryptData } from "@/utils/crypto.utils";
import { downloadBlob } from "@/utils/file.utils";
import { getTimestamp } from "@/utils/date.utils";

export class AddressExportService {
  async exportAddresses(options: ExportOptions): Promise<ExportResult> {
    console.log("[Export] exportAddresses 开始");
    const addresses = await addressRepository.findAll();

    if (addresses.length === 0) {
      throw new Error("没有可导出的地址数据");
    }

    console.log("[Export] 找到地址数量:", addresses.length);

    let blob: Blob;
    let encrypted = false;
    let compressed = false;

    if (options.format === "csv") {
      blob = await this.exportAsCSV(addresses);
    } else {
      const result = await this.exportAsCustom(addresses, options);
      blob = result.blob;
      encrypted = result.encrypted;
      compressed = result.compressed;
    }

    const filename = this.generateFilename(options.format);
    console.log("[Export] 生成文件名:", filename);
    console.log("[Export] Blob 大小:", blob.size);

    try {
      downloadBlob(blob, filename);
      console.log("[Export] downloadBlob 调用完成");
    } catch (error) {
      console.error("[Export] downloadBlob 调用失败:", error);
      throw error;
    }

    console.log("[Export] exportAddresses 成功完成");
    return {
      success: true,
      filename,
      count: addresses.length,
      encrypted,
      compressed,
    };
  }

  private exportAsCSV(addresses: Address[]): Promise<Blob> {
    const headers = "CALL,ADDRESS,ZIP,NOTE,OUTSEA";
    const rows = addresses.map((addr) => {
      const call = addr.call;
      const address = this.escapeCSVField(addr.address);
      const zip = addr.zip;
      const note = addr.status === "invalid" ? "无效地址" : "";
      const outsea = addr.outsea ? "Y" : "";
      return `${call},"${address}",${zip},${note},${outsea}`;
    });

    const csvContent = [headers, ...rows].join("\n");
    return Promise.resolve(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
  }

  private async exportAsCustom(
    addresses: Address[],
    options: ExportOptions
  ): Promise<{ blob: Blob; encrypted: boolean; compressed: boolean }> {
    console.log("[Export] exportAsCustom 开始");
    console.log("[Export] 地址数量:", addresses.length);
    console.log("[Export] 导出选项:", options);

    const data: QAddrFileData = {
      version: "1.0.0",
      exportedAt: new Date().toISOString(),
      addresses,
    };

    const jsonData = JSON.stringify(data);
    console.log("[Export] JSON 数据长度:", jsonData.length);
    let dataBytes = new TextEncoder().encode(jsonData);
    console.log("[Export] 初始数据字节数:", dataBytes.length);

    let flags = 0;
    let encrypted = false;
    let compressed = false;

    if (options.compressed) {
      console.log("[Export] 开始压缩数据");
      const compressedData = compressData(jsonData);
      dataBytes = new Uint8Array(compressedData.buffer);
      flags |= EXPORT_CONSTANTS.FLAGS.COMPRESSED;
      compressed = true;
      console.log("[Export] 压缩后数据长度:", dataBytes.length);
      console.log("[Export] 压缩率:", ((1 - dataBytes.length / jsonData.length) * 100).toFixed(2) + '%');
    }

    if (options.encrypted && options.password) {
      console.log("[Export] 开始加密数据");
      console.log("[Export] 密码长度:", options.password.length);
      try {
        const encryptedResult = await encryptData(dataBytes, options.password);
        dataBytes = this.combineEncryptedData(encryptedResult);
        flags |= EXPORT_CONSTANTS.FLAGS.ENCRYPTED;
        encrypted = true;
        console.log("[Export] 加密后数据长度:", dataBytes.length);
      } catch (error) {
        console.error("[Export] 加密失败:", error);
        throw error;
      }
    }

    const header = this.createHeader(flags, dataBytes.length);
    console.log("[Export] Header 创建成功，标志位:", flags.toString(2));
    const fileBytes = this.combineHeaderAndData(header, dataBytes);
    console.log("[Export] 最终文件大小:", fileBytes.length, "字节");

    return {
      blob: new Blob([fileBytes.buffer.slice(0) as ArrayBuffer], { type: "application/octet-stream" }),
      encrypted,
      compressed,
    };
  }

  private createHeader(flags: number, dataLength: number): Uint8Array {
    const header = new Uint8Array(10);

    const magicBytes = new TextEncoder().encode(EXPORT_CONSTANTS.MAGIC_NUMBER);
    header.set(magicBytes, 0);
    header[4] = EXPORT_CONSTANTS.VERSION;
    header[5] = flags;

    const dataLengthBytes = new DataView(new ArrayBuffer(4));
    dataLengthBytes.setUint32(0, dataLength, false);
    header.set(new Uint8Array(dataLengthBytes.buffer), 6);

    return header;
  }

  private combineEncryptedData(encryptedData: {
    encrypted: Uint8Array;
    salt: Uint8Array;
    iv: Uint8Array;
    authTag: Uint8Array;
  }): Uint8Array<ArrayBuffer> {
    const { encrypted, salt, iv, authTag } = encryptedData;
    const combined = new Uint8Array(
      salt.length + iv.length + authTag.length + encrypted.length
    );

    let offset = 0;
    combined.set(salt, offset);
    offset += salt.length;
    combined.set(iv, offset);
    offset += iv.length;
    combined.set(authTag, offset);
    offset += authTag.length;
    combined.set(encrypted, offset);

    return combined;
  }

  private combineHeaderAndData(
    header: Uint8Array,
    data: Uint8Array
  ): Uint8Array {
    const combined = new Uint8Array(header.length + data.length);
    combined.set(header);
    combined.set(data, header.length);
    return combined;
  }

  private escapeCSVField(field: string): string {
    return field.replace(/"/g, '""');
  }

  private generateFilename(format: string): string {
    const timestamp = getTimestamp();
    const extension = format === "csv" ? "csv" : "qaddr";
    return `地址导出_${timestamp}.${extension}`;
  }
}

export const addressExportService = new AddressExportService();
